import { useQuery } from '@tanstack/react-query'
import { accountsApi, engineApi } from '../api/client'
import { deriveBooks } from '../lib/deriveBookRole'
import type { Book, BookRole } from '../types'

export interface UseBooksResult {
  books: Book[]
  isLoading: boolean
  isError: boolean
  byRole: (role: BookRole) => Book | undefined
  byId: (id: number) => Book | undefined
}

/**
 * Composes the account registry with engine/status's per-book roster into
 * a single, role-derived list of Books. The global QueryClient sets
 * staleTime: 5000 with no retry override, so retry is set explicitly here
 * (this is the standing rule for every new account-scoped query).
 */
export function useBooks(): UseBooksResult {
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.list,
    refetchInterval: 60000,
    retry: 1,
  })

  const engineStatusQuery = useQuery({
    queryKey: ['engine-status'],
    queryFn: engineApi.getStatus,
    refetchInterval: 60000,
    retry: 1,
  })

  // TanStack Query v5's `isLoading` (`isPending && isFetching`) and
  // `isError` (`status === 'error'`) leave a real, reachable gap: a query
  // that fails at least once and is then network-mode-PAUSED (retry
  // scheduled but `onlineManager` considers the browser offline) sits at
  // `status: 'pending', fetchStatus: 'paused'` indefinitely — `isPending`
  // is true but `isFetching` is false, so `isLoading` reads false, and
  // `status` never reaches `'error'` so `isError` also reads false.
  // Verified live (WARGAME 18 Phase 2 fix round 1, dev-bypass preview with
  // no DASHBOARD_AGENT_TOKEN): a 401 on `/v1/accounts` left the query
  // paused indefinitely with `fetchFailureCount: 1`, `error: null`. A
  // naive isLoading/isError consumer (MobileBottomNav's three-tab-state
  // derivation) reads that as "cleanly resolved, no book for this role" —
  // the exact "API failure reads as not-created" bug the WARGAME 18 D2.3
  // lock forbids.
  //
  // `isPaused && data === undefined` closes the gap: it also covers the
  // narrower case where the *first* attempt never even ran because the
  // browser was offline at mount (`fetchFailureCount: 0` there), and it
  // deliberately does NOT trip during a background refetch of a query that
  // already has good data — a transient offline blip mid-session correctly
  // keeps showing the last-known-good roster rather than flipping a
  // resolved book list to "unavailable".
  const accountsFailing =
    accountsQuery.isError || (accountsQuery.isPaused && accountsQuery.data === undefined)
  const engineStatusFailing =
    engineStatusQuery.isError ||
    (engineStatusQuery.isPaused && engineStatusQuery.data === undefined)

  const isLoading = accountsQuery.isLoading || engineStatusQuery.isLoading
  const isError = accountsFailing || engineStatusFailing

  const accounts = accountsQuery.data ?? []
  const bookStatuses = engineStatusQuery.data?.books ?? []
  const books = accounts.length > 0 ? deriveBooks(accounts, bookStatuses) : []

  return {
    books,
    isLoading,
    isError,
    byRole: (role) => books.find((b) => b.role === role),
    byId: (id) => books.find((b) => b.id === id),
  }
}
